/**
 * §7.2 «Карточка сотрудника» /staff/{id} (manager v3).
 *
 * Воспроизводит мокап 7.2:
 *  - header: ФИО + 3 действия «Сменить роль» / «Уволить» / «Изменить»;
 *  - карточка «Личные»: большой аватар-инициалы, ФИО, роль · отдел, since-дата,
 *    контакты (Телефон / Email / Telegram);
 *  - «Расписание · ближайшие 7 дней» и «Производительность» — показываются
 *    ТОЛЬКО если бэк отдаёт данные; иначе graceful-заглушка (без выдуманных
 *    цифр, как требует ТЗ).
 *
 * Действия:
 *  - «Изменить» / «Сменить роль» → форма правки (отдел/должность, PATCH members);
 *  - «Уволить» → confirm-модал → POST members/{id}/terminate.
 *
 * Бэк: useStaffMember (GET members/{id}), useUpdateStaffMember (PATCH),
 * useTerminateStaffMember (POST terminate), useStaffDepartments (для смены отдела).
 */
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "@hsc/ui";
import type { StaffMember } from "@hsc/types";
import { initials } from "../shell/shell-util.js";
import { QrCanvas } from "../guest-access/QrCanvas.js";
import {
  useStaffMember,
  useStaffDepartments,
  useUpdateStaffMember,
  useTerminateStaffMember,
  useStaffTgBindLink,
} from "./useStaff.js";

function formatSince(iso: string | undefined): string {
  if (iso == null || iso === "") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" });
}

function memberName(m: StaffMember): string {
  const dn = (m as { display_name?: string }).display_name;
  if (typeof dn === "string" && dn.trim() !== "") return dn.trim();
  return m.position_title;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function StaffDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const memberQ = useStaffMember(id);
  const deptsQ = useStaffDepartments();
  const update = useUpdateStaffMember();
  const terminate = useTerminateStaffMember();
  const tgBind = useStaffTgBindLink();

  const [editing, setEditing] = useState(false);
  const [confirmTerminate, setConfirmTerminate] = useState(false);
  const [tgBinding, setTgBinding] = useState(false);
  const [editDept, setEditDept] = useState("");
  const [editPosition, setEditPosition] = useState("");
  const [termReason, setTermReason] = useState("");

  const deptName = useMemo(() => {
    const m = memberQ.data;
    if (!m) return "";
    return deptsQ.data?.find((d) => d.id === m.department_id)?.name ?? "";
  }, [memberQ.data, deptsQ.data]);

  if (memberQ.isLoading) {
    return (
      <section aria-busy="true" data-testid="staff-detail-loading">
        <h1 className="hsc-page__title">Карточка сотрудника</h1>
        <Skeleton height={160} radius={16} />
      </section>
    );
  }

  if (memberQ.isError || !memberQ.data) {
    return (
      <section data-testid="staff-detail-error">
        <h1 className="hsc-page__title">Карточка сотрудника</h1>
        <div className="staff-empty" role="alert">
          Не удалось загрузить карточку сотрудника.
        </div>
      </section>
    );
  }

  const m = memberQ.data;
  const name = memberName(m);
  // platform.users_identity.id сотрудника — нужен для привязки Telegram-входа
  // (#88). Не у всех строк персонала он есть (импорт/легаси) → кнопку прячем.
  const platformUserID = (m as { user_id?: string }).user_id;
  const canBindTelegram = typeof platformUserID === "string" && platformUserID !== "";
  const phone = (m as { phone?: string }).phone;
  const email = (m as { email?: string }).email;
  const telegram = (m as { telegram?: string }).telegram;
  const hasSchedule = Array.isArray((m as { schedule?: unknown[] }).schedule);
  const hasMetrics = (m as { metrics?: unknown }).metrics != null;

  const startEdit = (): void => {
    setEditDept(m.department_id);
    setEditPosition(m.position_title);
    setEditing(true);
  };

  const startTgBind = (): void => {
    if (!canBindTelegram) return;
    tgBind.reset();
    setTgBinding(true);
    tgBind.mutate(platformUserID as string);
  };

  const submitEdit = (e: React.FormEvent): void => {
    e.preventDefault();
    update.mutate(
      { id: m.id, input: { department_id: editDept, position_title: editPosition.trim() } },
      { onSuccess: () => setEditing(false) },
    );
  };

  const submitTerminate = (): void => {
    terminate.mutate(
      { id: m.id, input: { termination_date: todayISO(), ...(termReason ? { reason: termReason } : {}) } },
      {
        onSuccess: () => {
          setConfirmTerminate(false);
          navigate("/staff");
        },
      },
    );
  };

  // Привязка Telegram-входа (#88) — ОТДЕЛЬНЫЙ ЭКРАН с QR + deep-link и кнопкой
  // «Назад» снизу [[feedback_every_form_is_a_separate_screen]]. Менеджер
  // показывает сотруднику QR; сотрудник сканирует → бот привязывает аккаунт →
  // вход через Mini App. Токен одноразовый/15 мин — «Обновить» выпускает новый.
  if (tgBinding) {
    const link = tgBind.data?.deep_link ?? "";
    return (
      <section data-testid="staff-tgbind-page" className="staff-detail">
        <header className="staff-topbar">
          <div>
            <div className="staff-topbar__crumbs">
              <button type="button" className="staff-link" onClick={() => navigate("/staff")} data-testid="staff-tgbind-crumb-staff">
                Персонал
              </button>{" "}
              /{" "}
              <button type="button" className="staff-link" onClick={() => setTgBinding(false)} data-testid="staff-tgbind-crumb-member">
                {name}
              </button>{" "}
              / Привязка Telegram
            </div>
            <h1 className="hsc-page__title staff-topbar__title">Привязать Telegram</h1>
          </div>
        </header>

        <article className="ta-card staff-profile" data-testid="staff-tgbind-screen">
          {tgBind.isPending && (
            <p className="staff-detail__stub" data-testid="staff-tgbind-loading">
              Готовим ссылку привязки…
            </p>
          )}
          {tgBind.isError && (
            <p role="alert" className="staff-invite__msg staff-invite__msg--err" data-testid="staff-tgbind-error">
              Не удалось выпустить ссылку. Проверьте, что у отеля настроен
              Telegram-бот, и попробуйте снова.
            </p>
          )}
          {tgBind.isSuccess && link !== "" && (
            <>
              <p className="staff-modal__text">
                Сотрудник <b>{name}</b> сканирует QR-код или открывает ссылку в
                Telegram — после нажатия «Старт» аккаунт привязывается, и вход в
                рабочий кабинет выполняется через бот/Mini App. Ссылка
                одноразовая и действует 15 минут.
              </p>
              <div className="ga-qr-wrap" data-testid="staff-tgbind-qr-wrap">
                <QrCanvas value={link} size={240} testId="staff-tgbind-qr" />
              </div>
              <div className="ta-field">
                <label className="hsc-input__label" htmlFor="tgbind-link">Ссылка-приглашение</label>
                <input
                  id="tgbind-link"
                  className="ta-input"
                  data-testid="staff-tgbind-link"
                  readOnly
                  value={link}
                  onFocus={(e) => e.currentTarget.select()}
                />
              </div>
            </>
          )}
          <div className="room-edit__footer">
            <button
              type="button"
              className="staff-btn"
              onClick={() => tgBind.mutate(platformUserID as string)}
              disabled={tgBind.isPending}
              data-testid="staff-tgbind-refresh"
            >
              {tgBind.isPending ? "Обновляем…" : "Обновить ссылку"}
            </button>
            <button type="button" className="staff-btn staff-btn--ghost" onClick={() => setTgBinding(false)} data-testid="staff-tgbind-back">
              Назад
            </button>
          </div>
        </article>
      </section>
    );
  }

  // Правка сотрудника (отдел/должность) — ОТДЕЛЬНЫЙ ЭКРАН (не модалка над
  // карточкой): родитель рендерит ТОЛЬКО форму, кнопки «Сохранить»/«Назад»
  // снизу — [[feedback_every_form_is_a_separate_screen]].
  if (editing) {
    return (
      <section data-testid="staff-edit-page" className="staff-detail">
        <header className="staff-topbar">
          <div>
            <div className="staff-topbar__crumbs">
              <button type="button" className="staff-link" onClick={() => navigate("/staff")} data-testid="staff-edit-crumb-staff">
                Персонал
              </button>{" "}
              /{" "}
              <button type="button" className="staff-link" onClick={() => setEditing(false)} data-testid="staff-edit-crumb-member">
                {name}
              </button>{" "}
              / Изменить
            </div>
            <h1 className="hsc-page__title staff-topbar__title">Изменить сотрудника</h1>
          </div>
        </header>

        <article className="ta-card staff-profile" data-testid="staff-edit-screen">
          <form onSubmit={submitEdit} className="staff-edit-form">
            <div className="ta-field">
              <label className="hsc-input__label" htmlFor="edit-dept">Отдел</label>
              <select
                id="edit-dept"
                className="ta-select"
                data-testid="staff-edit-department"
                value={editDept}
                onChange={(e) => setEditDept(e.target.value)}
              >
                {(deptsQ.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div className="ta-field">
              <label className="hsc-input__label" htmlFor="edit-position">Должность</label>
              <input
                id="edit-position"
                className="ta-input"
                data-testid="staff-edit-position"
                value={editPosition}
                onChange={(e) => setEditPosition(e.target.value)}
              />
            </div>
            {update.isError && (
              <p role="alert" className="staff-invite__msg staff-invite__msg--err">
                Не удалось сохранить изменения.
              </p>
            )}
            <div className="room-edit__footer">
              <button type="submit" className="staff-btn" disabled={update.isPending} data-testid="staff-edit-save">
                {update.isPending ? "Сохраняем…" : "Сохранить"}
              </button>
              <button type="button" className="staff-btn staff-btn--ghost" onClick={() => setEditing(false)} data-testid="staff-edit-back">
                Назад
              </button>
            </div>
          </form>
        </article>
      </section>
    );
  }

  // Увольнение — ОТДЕЛЬНЫЙ ЭКРАН (есть поле «причина» → это форма, не Да/Нет).
  if (confirmTerminate) {
    return (
      <section data-testid="staff-terminate-page" className="staff-detail">
        <header className="staff-topbar">
          <div>
            <div className="staff-topbar__crumbs">
              <button type="button" className="staff-link" onClick={() => navigate("/staff")} data-testid="staff-term-crumb-staff">
                Персонал
              </button>{" "}
              /{" "}
              <button type="button" className="staff-link" onClick={() => setConfirmTerminate(false)} data-testid="staff-term-crumb-member">
                {name}
              </button>{" "}
              / Увольнение
            </div>
            <h1 className="hsc-page__title staff-topbar__title">Уволить сотрудника?</h1>
          </div>
        </header>

        <article className="ta-card staff-profile" data-testid="staff-terminate-screen">
          <p className="staff-modal__text">
            {name} будет уволен(а) и потеряет доступ. Активные заявки нужно переназначить. Действие необратимо.
          </p>
          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="term-reason">Причина (необязательно)</label>
            <input
              id="term-reason"
              className="ta-input"
              data-testid="staff-terminate-reason"
              value={termReason}
              onChange={(e) => setTermReason(e.target.value)}
              placeholder="Например, по собственному желанию"
            />
          </div>
          {terminate.isError && (
            <p role="alert" className="staff-invite__msg staff-invite__msg--err">
              Не удалось уволить сотрудника.
            </p>
          )}
          <div className="room-edit__footer">
            <button
              type="button"
              className="staff-btn staff-btn--danger"
              onClick={submitTerminate}
              disabled={terminate.isPending}
              data-testid="staff-terminate-confirm"
            >
              {terminate.isPending ? "Увольняем…" : "Уволить"}
            </button>
            <button type="button" className="staff-btn staff-btn--ghost" onClick={() => setConfirmTerminate(false)} data-testid="staff-terminate-back">
              Назад
            </button>
          </div>
        </article>
      </section>
    );
  }

  return (
    <section data-testid="staff-detail-page" className="staff-detail">
      <header className="staff-topbar">
        <div>
          <div className="staff-topbar__crumbs">
            <button type="button" className="staff-link" onClick={() => navigate("/staff")} data-testid="staff-detail-back">
              Персонал
            </button>{" "}
            / {name}
          </div>
          <h1 className="hsc-page__title staff-topbar__title">{name}</h1>
        </div>
        <div className="staff-topbar__actions">
          <button type="button" className="staff-btn staff-btn--ghost" data-testid="staff-detail-changerole" onClick={startEdit}>
            Сменить роль
          </button>
          <button
            type="button"
            className="staff-btn staff-btn--ghost"
            data-testid="staff-detail-terminate"
            onClick={() => setConfirmTerminate(true)}
            disabled={m.termination_date != null && m.termination_date !== ""}
          >
            Уволить
          </button>
          {canBindTelegram && (
            <button
              type="button"
              className="staff-btn staff-btn--ghost"
              data-testid="staff-detail-tgbind"
              onClick={startTgBind}
            >
              Привязать Telegram
            </button>
          )}
          <button type="button" className="staff-btn" data-testid="staff-detail-edit" onClick={startEdit}>
            Изменить
          </button>
        </div>
      </header>

      <div className="staff-detail__grid">
        {/* Личные данные */}
        <article className="ta-card staff-profile">
          <div className="staff-profile__head">
            <span className="ta-avatar ta-avatar--lg staff-profile__avatar" aria-hidden="true">
              {initials(name)}
            </span>
            <div>
              <h2 className="staff-profile__name">{name}</h2>
              <p className="staff-profile__role">
                {m.position_title}
                {deptName ? ` · ${deptName}` : ""}
              </p>
              <p className="staff-profile__since">с {formatSince(m.hire_date)}</p>
            </div>
          </div>
          <hr className="staff-profile__divider" />
          <dl className="staff-profile__contacts">
            <div>
              <dt>Телефон</dt>
              <dd>{phone != null && phone !== "" ? phone : "не указан"}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{email != null && email !== "" ? email : "не указан"}</dd>
            </div>
            <div>
              <dt>Telegram</dt>
              <dd>
                {telegram != null && telegram !== "" ? (
                  <>
                    {telegram} <span className="ta-chip ta-chip--ok">подключён</span>
                  </>
                ) : (
                  <span className="ta-chip ta-chip--muted">не подключён</span>
                )}
              </dd>
            </div>
            <div>
              <dt>Статус</dt>
              <dd>
                {m.termination_date != null && m.termination_date !== "" ? (
                  <span className="ta-chip ta-chip--muted">уволен</span>
                ) : m.is_active ? (
                  <span className="ta-chip ta-chip--ok">активен</span>
                ) : (
                  <span className="ta-chip ta-chip--muted">не активен</span>
                )}
              </dd>
            </div>
            {m.employee_code != null && m.employee_code !== "" && (
              <div>
                <dt>Табельный №</dt>
                <dd>{m.employee_code}</dd>
              </div>
            )}
          </dl>
        </article>

        {/* Расписание / производительность — graceful */}
        <article className="ta-card staff-profile">
          <h3 className="staff-profile__section-title">Расписание · ближайшие 7 дней</h3>
          {hasSchedule ? null : (
            <p className="staff-detail__stub" data-testid="staff-schedule-stub">
              Расписание появится после публикации графика смен.
            </p>
          )}
          <hr className="staff-profile__divider" />
          <h3 className="staff-profile__section-title">Производительность · последние 30 дней</h3>
          {hasMetrics ? null : (
            <p className="staff-detail__stub" data-testid="staff-metrics-stub">
              Статистика появится после первых обработанных заявок и отзывов.
            </p>
          )}
        </article>
      </div>

    </section>
  );
}
